/**
 * True if the target is a real text-entry element (input/textarea/
 * contentEditable) — used to avoid stealing keystrokes meant for an
 * unrelated text field elsewhere on the page.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!(
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el?.isContentEditable
  );
}

/**
 * Class names identifying "modal-like" UI that global keyboard shortcuts
 * (Escape, Ctrl+Z, Delete, ...) must not fire through — an Element Plus
 * dialog (.el-dialog, .el-overlay), or our own custom keypad panel
 * (.keypad-panel, NumericKeypadModal.vue — built without Element Plus,
 * so it needs its own marker here). Shared by every window/document-level
 * shortcut listener (KeyHandler, SequentialDragger, TwoPointDragger,
 * BasePointDragger) so a new non-Element-Plus overlay only requires
 * updating this one selector.
 *
 * Deliberately NOT used by the keypad's own key handler: that handler wants
 * the opposite check (isTypingTarget only) — it IS the modal, so it must
 * keep listening while it has focus, not skip because of it.
 */
const SHORTCUT_BLOCKING_SELECTOR = ".el-dialog, .el-overlay, .keypad-panel";

/**
 * Escape is the one shortcut the keypad lets through: the pad has no use for
 * it (it never closes on Escape — only its × button does), and swallowing it
 * would leave no keyboard way to finish a polyline or cancel a command while
 * the pad holds focus, which it does most of the time. Real dialogs still
 * block it, since there Escape belongs to the dialog.
 */
const ESCAPE_BLOCKING_SELECTOR = ".el-dialog, .el-overlay";

/**
 * @param key The pressed key, so Escape can be let through from the keypad.
 * Omitting it keeps the strictest check.
 */
export function isShortcutBlockingTarget(
  target: EventTarget | null,
  key?: string
): boolean {
  const el = target as HTMLElement | null;
  const selector =
    key === "Escape" ? ESCAPE_BLOCKING_SELECTOR : SHORTCUT_BLOCKING_SELECTOR;
  return isTypingTarget(target) || !!el?.closest(selector);
}
